// Menu
import React, { useContext, useReducer, useState, useEffect } from "react";

// Scroll
import PerfectScrollbar from "react-perfect-scrollbar";
import Collapse from 'react-bootstrap/Collapse';

// Link
import { Link, NavLink, useLocation } from "react-router-dom";

import { MenuList } from './Menu';
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import LocalStorageType from "../../../constants/LocalStorageType";
import Copyright from "../Copyright";
import SaaSBillingService from "../../../services/superadmin/SaaSBillingService";
import "./SideBar.css";

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
}

const hasModuleAccess = (availableModules, requiredModule) => {
  return Array.isArray(availableModules) && availableModules.includes(requiredModule);
};

const hasMenuKeyAccess = (menuPermissions, module, menuKey) => {
  return Array.isArray(menuPermissions)
    && menuPermissions.some(item => item.permission === module && item.menuKey === menuKey);
};

const normalizeRoute = (route) => {
  if (!route) return "";
  return route.startsWith('/') ? route : `/${route}`;
};

const planPermissionKey = (module, menuKey) => {
  if (!module || !menuKey) return null;
  return `${String(module).toLowerCase()}.${menuKey}.view`;
};

// Verifica se uma rota de menu corresponde ao caminho atual (exato ou sub-rota)
const routeMatches = (menuRoute, currentPath, exact = false) => {
  if (!menuRoute) return false;
  const normalizedMenuRoute = normalizeRoute(menuRoute);
  if (exact) {
    return currentPath === normalizedMenuRoute;
  }
  return currentPath === normalizedMenuRoute || currentPath.startsWith(`${normalizedMenuRoute}/`);
};

const routeHasMoreSpecificSiblingMatch = (menuItem, siblings = [], currentPath) => {
  const normalizedRoute = normalizeRoute(menuItem.to);

  return siblings.some((sibling) => {
    if (sibling === menuItem || !sibling.to) return false;
    const normalizedSiblingRoute = normalizeRoute(sibling.to);
    return normalizedSiblingRoute.length > normalizedRoute.length
      && routeMatches(sibling.to, currentPath, sibling.exact);
  });
};

const menuItemMatches = (menuItem, siblings, currentPath) => {
  return routeMatches(menuItem.to, currentPath, menuItem.exact)
    && !routeHasMoreSpecificSiblingMatch(menuItem, siblings, currentPath);
};

// Função para encontrar o menu ativo baseado na URL atual
const findActiveMenu = (menuList, currentPath) => {
  for (const menu of menuList) {
    // Verifica se é um menu sem submenus e corresponde à rota
    if (!menu.content || menu.content.length === 0) {
      if (routeMatches(menu.to, currentPath, menu.exact)) {
        return { active: "", activeSubmenu: "" };
      }
    } else {
      // Verifica os submenus
      for (const submenu of menu.content) {
        if (menuItemMatches(submenu, menu.content, currentPath)) {
          return { active: menu.title, activeSubmenu: "" };
        }
        
        // Verifica os sub-submenus
        if (submenu.content && submenu.content.length > 0) {
          for (const subsubmenu of submenu.content) {
            if (menuItemMatches(subsubmenu, submenu.content, currentPath)) {
              return { active: menu.title, activeSubmenu: submenu.title };
            }
          }
        }
      }
    }
  }
  return { active: "", activeSubmenu: "" };
};

const SideBar = () => {

  const { t } = useTranslation();
  const location = useLocation();

  // Recupera o usuário logado.
  // Usamos o usuário para checar quais menus, a partir das permissões, ele pode usar.
  const loggedUser = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS));
  const [planPermissions, setPlanPermissions] = useState(null);

  useEffect(() => {
    if (loggedUser?.superAdmin) return;
    SaaSBillingService.currentPlanPermissions()
      // restricted=false (cliente sem contrato/plano) mantém o menu liberado.
      .then(response => setPlanPermissions(response.data?.restricted ? (response.data.keys || []) : null))
      .catch(() => setPlanPermissions(null));
  }, []);

  // Função para fechar o menu mobile ao clicar em um item
  const closeMobileMenu = () => {
    // Verifica se está em mobile (breakpoint 1024px)
    if (window.innerWidth < 1024) {
      const mainwrapper = document.querySelector("#main-wrapper");
      if (mainwrapper && mainwrapper.classList.contains('menu-toggle')) {
        mainwrapper.classList.remove("menu-toggle");
      }
    }
  };

  const canDisplayMenuItem = (menuItem, user) => {
    // Se o menu for exclusivo de superAdmin
    if (menuItem.superAdminOnly) {
      return user?.superAdmin === true;
    }

    if (menuItem.adminOnly && !(user?.admin === true || user?.superAdmin === true)) {
      return false;
    }

    if (!menuItem.module) {
      return true;
    }

    // O super admin não depende do plano da empresa que está acessando: sem esta saída
    // o menu sumia inteiro em empresas sem contrato (companyPermissions vazio).
    if (user?.superAdmin === true) {
      return true;
    }

    const companyModules = user?.companyPermissions;
    const userModules = user?.permissions;
    const hasCompanyModuleConfig = Array.isArray(companyModules);
    const companyAllowsModule = !hasCompanyModuleConfig
      || hasModuleAccess(companyModules, menuItem.module);
    const userAllowsModule = hasModuleAccess(userModules, menuItem.module);

    if (user?.admin === true) {
      return companyAllowsModule;
    }

    return companyAllowsModule && userAllowsModule;
  }

  // Verifica se um item de submenu (dentro de um módulo) pode ser exibido para o usuário.
  const canDisplaySubMenuItem = (menuItem, submenu, user) => {
    if (!menuItem.module || !submenu.menuKey) {
      return true;
    }

    if (user?.admin === true || user?.superAdmin === true) {
      return true;
    }

    // Sessões antigas (login anterior a essa funcionalidade) ainda não têm
    // menuPermissions no localStorage: nesse caso não restringe até o próximo login.
    if (user?.menuPermissions === undefined) {
      return true;
    }

    return hasMenuKeyAccess(user.menuPermissions, menuItem.module, submenu.menuKey);
  };

  const isPlanMenuLocked = (menuItem, submenu) => {
    if (loggedUser?.superAdmin || !Array.isArray(planPermissions) || !menuItem.module || !submenu.menuKey) return false;
    return !planPermissions.includes(planPermissionKey(menuItem.module, submenu.menuKey));
  };

  // Verifica quais os menus deverão ser exibidos para o usuário
  const VISIBLE_MENU = MenuList
    .filter(item => canDisplayMenuItem(item, loggedUser))
    .map(item => item.content
      ? { ...item, content: item.content.filter(submenu => canDisplaySubMenuItem(item, submenu, loggedUser)) }
      : item)
    .filter(item => !item.module || !item.content || item.content.length > 0);

  // Inicializa o estado do menu baseado na URL atual ou no localStorage
  const getInitialMenuState = () => {
    // Tenta recuperar o estado salvo do localStorage
    const savedState = localStorage.getItem(LocalStorageType.MENU_STATE);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Valida se o menu salvo ainda existe na lista de menus visíveis
        const menuExists = VISIBLE_MENU.some(menu => menu.title === parsed.active);
        if (menuExists) {
          return parsed;
        }
      } catch (e) {
        console.error("Erro ao recuperar estado do menu:", e);
      }
    }
    
    // Se não há estado salvo ou é inválido, determina baseado na URL atual
    const activeMenuState = findActiveMenu(VISIBLE_MENU, location.pathname);
    return activeMenuState;
  };

  const [state, setState] = useReducer(reducer, getInitialMenuState());

  // Atualiza o menu ativo quando a rota muda
  useEffect(() => {
    const activeMenuState = findActiveMenu(VISIBLE_MENU, location.pathname);
    setState(activeMenuState);
    // Salva o estado no localStorage
    localStorage.setItem(LocalStorageType.MENU_STATE, JSON.stringify(activeMenuState));
  }, [location.pathname]); // Removido VISIBLE_MENU das dependências para evitar re-renderizações desnecessárias

  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  // For scroll
  const [hideOnScroll, setHideOnScroll] = useState(true)
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y
      if (isShow !== hideOnScroll) setHideOnScroll(isShow)
    },
    [hideOnScroll]
  )

  const handleMenuActive = status => {
    const newActive = state.active === status ? "" : status;
    const newState = { active: newActive };
    setState(newState);
    
    // Salva o estado no localStorage
    localStorage.setItem(LocalStorageType.MENU_STATE, JSON.stringify({
      active: newActive,
      activeSubmenu: state.activeSubmenu
    }));
  }

  const handleSubmenuActive = (status) => {
    const newActiveSubmenu = state.activeSubmenu === status ? "" : status;
    const newState = { activeSubmenu: newActiveSubmenu };
    setState(newState);
    
    // Salva o estado no localStorage
    localStorage.setItem(LocalStorageType.MENU_STATE, JSON.stringify({
      active: state.active,
      activeSubmenu: newActiveSubmenu
    }));
  }
  // Menu dropdown list End

  /// Path
  let path = window.location.pathname;
  path = path.split("/");
  path = path[path.length - 1];
  /// Active menu

  return (
    <div
      className={`dlabnav  border-right ${iconHover} ${sidebarposition.value === "fixed" &&
        sidebarLayout.value === "horizontal" &&
        headerposition.value === "static"
        ? hideOnScroll > 120
          ? "fixed"
          : ""
        : ""
        }`}
    >
      <PerfectScrollbar className="dlabnav-scroll">
        <ul className="metismenu mb-5" id="menu">
          <li className="menu-title">Menu</li>
          {VISIBLE_MENU.map((data) => {
            let menuClass = data.classsChange;

            if (menuClass === "menu-title") {
              return (
                <li key={data.title} className={menuClass}>{t(data.title)}</li>
              );
            } else {
              return (
                <li
                  key={data.title}
                  className={` ${state.active === data.title ? 'mm-active' : ''}`}
                >
                  {data.content && data.content.length > 0 ? (
                    <Link
                      to={"#"}
                      className="has-arrow"
                      onClick={() => handleMenuActive(data.title)}
                    >
                      <i className={data.iconStyle}></i>
                      <span className="nav-text">{t(data.title)}</span>
                    </Link>
                  ) : (
                    <NavLink 
                      to={data.to}
                      className={({ isActive }) => isActive ? 'mm-active' : ''}
                      onClick={closeMobileMenu}
                    >
                      <i className={data.iconStyle}></i>
                      <span className="nav-text">{t(data.title)}</span>
                    </NavLink>
                  )}
                  <Collapse in={state.active === data.title}>
                    <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                      {data.content && data.content.map((submenu) => {
                        const isSubmenuActive = menuItemMatches(submenu, data.content, location.pathname);
                        const planLocked = isPlanMenuLocked(data, submenu);

                        return (
                          <li
                            key={submenu.title}
                            className={`${state.activeSubmenu === submenu.title || (!submenu.content?.length && isSubmenuActive) ? "mm-active" : ""}`}
                          >
                            {submenu.content && submenu.content.length > 0 ? (
                              <NavLink
                                to={submenu.to}
                                className={() =>
                                  `${submenu.hasMenu ? 'has-arrow' : ''} ${isSubmenuActive ? 'mm-active' : ''} ${planLocked ? 'saas-menu-locked' : ''}`
                                }
                                title={planLocked ? 'Disponível em um plano superior' : undefined}
                                onClick={(event) => {
                                  if (planLocked) { event.preventDefault(); return; }
                                  handleSubmenuActive(submenu.title);
                                  closeMobileMenu();
                                }}
                              >
                                {t(submenu.title)}
                              </NavLink>
                            ) : (
                              <NavLink 
                                to={submenu.to}
                                className={() => `${isSubmenuActive ? 'mm-active' : ''} ${planLocked ? 'saas-menu-locked' : ''}`}
                                onClick={(event) => { if (planLocked) { event.preventDefault(); return; } closeMobileMenu(); }}
                                title={planLocked ? 'Disponível em um plano superior' : undefined}
                              >
                                {t(submenu.title)} {planLocked && <i className="fa-solid fa-lock ms-2" aria-label="Recurso bloqueado" />}
                              </NavLink>
                            )}
                            <Collapse in={state.activeSubmenu === submenu.title}>
                              <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                                {submenu.content && submenu.content.map((subsubmenu) => (
                                  <li key={subsubmenu.title}>
                                    <NavLink
                                      className={() => menuItemMatches(subsubmenu, submenu.content, location.pathname) ? 'mm-active' : ''}
                                      to={subsubmenu.to}
                                      onClick={closeMobileMenu}
                                    >
                                      {t(subsubmenu.title)}
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            </Collapse>
                          </li>
                        );
                      })}
                    </ul>
                  </Collapse>
                </li>
              );
            }
          })}
        </ul>
        <div className="mb-5">&nbsp;</div>
        {/*
        <div className="plus-box">
          <div className="d-flex align-items-center">
            <h5>Upgrade your Account to Get Free Voucher</h5>
          </div>
          <Link to={"#"} className="btn bg-white btn-sm">Upgrade</Link>
        </div>
        
        <div className="copyright mt-0">
          <Copyright heart={true}/>
        </div>
        */}
      </PerfectScrollbar>
    </div>
  );
};

export default SideBar;
